import { CClient, currencyCodeToSymbol } from '@common/utils'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import useDetectCountry from '@hooks/useDetectCountry'
import { Trans } from '@lingui/macro'
import React, { useEffect, useState } from 'react'
import { Check, X } from 'react-feather'
import * as Sentry from '@sentry/node'
import { FastlyImage } from '@components/common/image'
import classNames from 'classnames'
import { COURSE_FIRST_FOLD } from '../../lib/data/course-landing-page'
import { EUROPEAN_COUNTRY_CODES } from '../../lib/data/country'
import { SizeType } from '@components/atoms/button'

const CODINGAL_OFFERING = {
  prime: {
    title: 'Prime',
    subtitle: <Trans>Private 1-on-1 tutoring</Trans>,
    color: '#EB5757',
    points: [
      {
        offering: true,
        content: <Trans>1:1 personalized live classes (45 minutes each)</Trans>,
      },
      {
        offering: true,
        content: (
          <Trans>
            2–3 lessons per week — flexible scheduling tailored for your child
          </Trans>
        ),
      },
      {
        offering: true,
        content: <Trans>Personalized attention in every class</Trans>,
      },
      {
        offering: true,
        content: (
          <Trans>
            <b>Unlimited rescheduling</b> at the student’s convenience
          </Trans>
        ),
      },
    ],
  },
  premier: {
    title: 'Premier',
    subtitle: <Trans>Micro group (2–3 students)</Trans>,
    color: '#9B51E0',
    points: [
      {
        offering: true,
        content: (
          <Trans>Personalized live group classes (60 minutes each)</Trans>
        ),
      },
      {
        offering: true,
        content: (
          <Trans>2 lessons per week — 8 fun & engaging classes per month</Trans>
        ),
      },
      {
        offering: true,
        content: <Trans>Focused attention in every class</Trans>,
      },
      {
        offering: true,
        content: <Trans>Reschedule mutually with your class buddies</Trans>,
      },
    ],
  },
  plus: {
    title: 'Plus',
    subtitle: <Trans>Small group (4–5 students)</Trans>,
    color: '#2D9CDB',
    points: [
      {
        offering: true,
        content: (
          <Trans>Fun & engaging live group classes (60 minutes each)</Trans>
        ),
      },
      {
        offering: true,
        content: (
          <Trans>2 lessons per week — 8 fun & engaging classes per month</Trans>
        ),
      },
      {
        offering: true,
        content: <Trans>Focused attention in every class</Trans>,
      },
      {
        offering: false,
        content: <Trans>No rescheduling of classes available</Trans>,
      },
    ],
  },
}
const COURSE_FEATURES = [
  <Trans key={1}>
    A <strong>handpicked tutor</strong> matched to your child&apos;s curriculum,
    pace, and accent.
  </Trans>,
  <Trans key={2}>
    <strong>Two free</strong> monthly doubt sessions, <strong>live 1:1</strong>{' '}
    with their tutor.
  </Trans>,
  <Trans key={3}>
    <strong>Unlimited AI Tutor access</strong>, available anytime your child
    needs it.
  </Trans>,
  <Trans key={4}>
    A dedicated <strong>support team available 24x7</strong> for any questions.
  </Trans>,
  <Trans key={5}>
    <strong>Lifetime access</strong> to all class recordings and worksheets.
  </Trans>,
  <Trans key={6}>
    A{' '}
    <strong>
      <u>STEM.org</u>
    </strong>{' '}
    accredited certificate earned at every milestone.
  </Trans>,
  <Trans key={7}>
    <strong>Worksheets and quizzes</strong> after every class to reinforce the
    learning.
  </Trans>,
  <Trans key={8}>
    <strong>Monthly progress reports</strong> and{' '}
    <strong>regular parent-teacher meetings</strong> to keep you in the loop.
  </Trans>,
  <Trans key={9}>
    <strong>Flexible payment options</strong> to suit your budget.
  </Trans>,
  <Trans key={10}>
    A <strong>100% money-back guarantee</strong> if the first class isn&apos;t
    the right fit.
  </Trans>,
  <Trans key={11}>
    A <strong>personalized dashboard</strong> to track progress, access
    recordings, and review worksheets.
  </Trans>,
]
const CODINGAL_PLAN = {
  MONTHLY: {
    label: 'Monthly',
    discount: {
      prime: 0.3,
      premier: 0.25,
      plus: 0.2,
    },
  },
  FULL_PAYMENT: {
    label: 'Full Payment',
    discount: {
      prime: 0.35,
      premier: 0.3,
      plus: 0.25,
    },
  },
}

export type CodingalSubscriptionType = {
  courseName?: string
  compact?: boolean
  urlQuery?: string
  subject?: 'Coding' | 'Math' | 'ELA'
  ctaSize?: SizeType
  features?: React.ReactNode[]
}

const CodingalSubscription = ({
  courseName,
  compact = false,
  subject = 'Coding',
  urlQuery,
  ctaSize,
  features,
}: CodingalSubscriptionType) => {
  const { countryCode, currency } = useDetectCountry()
  const subsCode = Object.keys(CODINGAL_OFFERING)
  const plan = Object.keys(CODINGAL_PLAN)
  const [activeTab, setActiveTab] = useState<string>(
    courseName ? plan[0] : plan[1]
  )
  const [prices, setPrices] = useState<any>()
  async function handleFunctionPrices() {
    const code = EUROPEAN_COUNTRY_CODES.includes(countryCode || '')
      ? 'europe'
      : countryCode
    try {
      const q = `?country_code=${
        code ?? 'us'
      }&subject=${subject}&currency_code=${currency ?? 'USD'}`
      const endpoint = `/api/v1/pricing/service-prices/${q}`
      const response = await CClient(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      if (response.status === 200) {
        const data = await response.json()
        setPrices(data.prices)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  useEffect(() => {
    if (countryCode) handleFunctionPrices()
  }, [countryCode])

  const SubscriptionCard = ({ subscription }: { subscription: string }) => {
    const offer = CODINGAL_OFFERING[subscription]
    const discount = CODINGAL_PLAN[activeTab].discount[subscription]
    if (!prices || !prices[subscription]) {
      return null
    }
    const lessons = courseName ? COURSE_FIRST_FOLD[courseName]?.lessons ?? 0 : 0
    const cost = prices[subscription].pricePerSession * (courseName ? 0.8 : 1) // 0.8 factor for specialized courses
    const offerPrice = cost * (1 - discount)

    const monthly = offerPrice * 8 // 8 sessions in a month
    const monthlyTotal =
      cost * lessons * (1 - CODINGAL_PLAN.MONTHLY.discount[subscription])

    const fullPaymentPlan =
      cost * lessons * (1 - CODINGAL_PLAN.FULL_PAYMENT.discount[subscription])

    const fullCourseSave = monthlyTotal - fullPaymentPlan
    const discountPercentage = discount * 100

    return (
      <div
        className={classNames(
          'flex flex-col w-full rounded-lg bg-white border overflow-hidden relative items-center shadow hover:shadow-lg transition-all duration-200',
          { 'xl:w-64': compact }
        )}
        style={{ borderColor: offer.color }}
      >
        <h3
          className={classNames(
            'py-2 leading-tight font-600 w-full text-2xl text-center text-white',
            { 'lg:text-3xl': !compact }
          )}
          style={{ backgroundColor: offer.color }}
        >
          {offer.title}
        </h3>
        <div
          style={{ backgroundColor: `${offer.color}1A` }}
          className={`w-full flex flex-col items-center gap-2 ${
            compact ? 'p-1 py-3' : 'p-5'
          }`}
        >
          {prices && prices[subscription].pricePerSession !== 0 && (
            <>
              <div className="space-y-2">
                <div
                  className={classNames(
                    'leading-tight font-600 w-full text-2xl  text-center',
                    { 'lg:text-3xl': !compact }
                  )}
                >
                  <Trans>
                    {currencyCodeToSymbol(offerPrice, currency)}{' '}
                    <span
                      className={`text-sm ${compact ? '' : ''} text-grey-800`}
                    >
                      / session
                    </span>
                  </Trans>
                </div>

                <div
                  className={classNames(
                    'leading-tight font-600 w-full text-lg text-center text-grey-800 line-through',
                    {
                      'lg:text-2xl': !compact,
                    }
                  )}
                >
                  {currencyCodeToSymbol(cost, currency)}
                </div>
              </div>

              <div
                className={`py-1 bg-white text-grey-800 font-600 rounded-full mx-auto ${
                  compact ? 'px-3' : 'text-lg px-4'
                }`}
              >
                {discountPercentage}% off
              </div>
              {courseName && (
                <div className="text-grey-800 text-sm text-center">
                  {activeTab == plan[0] ? (
                    <>
                      <b>{currencyCodeToSymbol(monthly, currency)}</b> / month
                      (total discounted course fee:{' '}
                      <b>{currencyCodeToSymbol(monthlyTotal, currency)}</b>)
                    </>
                  ) : (
                    <>
                      Total discounted course fee:{' '}
                      <br className="hidden xl:block" />
                      <b>
                        {currencyCodeToSymbol(fullPaymentPlan, currency)}
                      </b>{' '}
                      (save{' '}
                      <b className="text-green-300">
                        {currencyCodeToSymbol(fullCourseSave, currency)}
                      </b>
                      )
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div
          className={`mx-auto w-full ${compact ? 'mt-3 px-3' : 'mt-5 px-8'}`}
        >
          <BookFreeClassBtn fullWidth size="md" variant="outline" />
        </div>
        <h4
          className={classNames('font-600 underline text-center pt-4 px-3', {
            'text-lg': !compact,
          })}
        >
          {offer.subtitle}
        </h4>
        <ul className={`flex flex-col ${compact ? 'gap-1 p-2' : 'gap-2 p-4'}`}>
          {offer.points.map((point, index) => (
            <li key={index} className="inline-flex items-baseline gap-2">
              <span
                className={`rounded-full inline-flex p- ${
                  point.offering ? 'bg-green-100' : 'bg-red-200'
                }`}
                style={{ padding: compact ? '1px' : '2px' }}
              >
                {point.offering ? (
                  <Check
                    height={!compact ? 13 : 10}
                    width={!compact ? 13 : 10}
                    strokeWidth={3}
                    className="text-white"
                  />
                ) : (
                  <X
                    height={!compact ? 13 : 10}
                    width={!compact ? 13 : 10}
                    strokeWidth={3}
                    className="text-white"
                  />
                )}{' '}
              </span>
              <span
                className={classNames('font-400', {
                  'text-lg': !compact,
                })}
              >
                {point.content}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  // Determine the default offering based on countryCode or explicitly provided offeringType

  // Initialize activeTab with logic to ensure 'plus' is selected for 'both'

  // Set the codingalSubscription based on the activeTab

  // Set activeTab to 'plus' if defaultSubscriptionType is 'both'
  if (!prices) return <></>
  return (
    <div className="text-grey">
      <div
        className={`container mx-auto my-12  text-grey flex flex-col gap-4 items-center ${
          compact ? 'lg:px-3' : 'lg:my-16 px-5'
        }`}
      >
        <h2
          className={classNames('text-2xl font-600 leading-tight mb-4', {
            'lg:text-4xl max-w-4xl mx-auto text-center': !compact,
            'text-left self-start': compact,
          })}
        >
          <Trans>
            3 flexible plans to suit your{' '}
            <span className="yellow-underline">
              child’s learning style & schedule
            </span>
          </Trans>
        </h2>

        {courseName && (
          <div className="mb-5 flex flex-col space-y-2 items-end">
            <div className="inline-flex rounded-lg bg-white border border-grey-400 flex-wrap justify-between lg:justify-center">
              {plan.map((e, i) => (
                <button
                  key={i}
                  className={`text-center rounded-md m-1 px-2 lg:px-6 lg:text-lg cursor-pointer focus:outline-none font-600 border relative ${
                    activeTab === e
                      ? 'text-orange border-orange-300 bg-orange-200'
                      : 'border-white'
                  } `}
                  onClick={() => setActiveTab(e)}
                >
                  <span>{CODINGAL_PLAN[e].label}</span>
                  {e === plan[1] && (
                    <div className="text-green-300 font-700 text-xxs lg:text-xs uppercase mx-auto rounded-full whitespace-no-wrap">
                      Up to 35% off
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {/** Disaplay Tab only if the courseofferingType is both */}
        <div
          className={`w-full flex flex-col justify-center ${
            compact ? 'gap-1 xl:flex-row' : 'gap-5 lg:flex-row'
          }`}
        >
          {subsCode.map((subscription, i) => (
            <SubscriptionCard key={i} subscription={subscription} />
          ))}
        </div>
        <div className="flex flex-col items-center mt-8">
          <div>
            <FastlyImage
              src={`/images/home/cody.png`}
              webpSrc={`/images/home/cody.webp`}
              alt={'cody'}
              height={111}
              width={97}
              className="h-13  object-cover object-top"
            />
          </div>
          <h2 className="bg-green-50 border border-green-300 p-4 px-8 rounded-xl text-lg font-600">
            <Trans>
              Complete a free trial lesson to unlock additional scholarships!
            </Trans>
          </h2>
        </div>

        <div
          className={classNames(
            'container p-4 sm:p-8 sm:pt-10 md:pt-12 border border-blue-100 rounded-2xl bg-blue-50 bg-opacity-25 space-y-4 relative',
            {
              'md:pl-8': compact,
              'md:pl-12': !compact,
            }
          )}
        >
          <div className="absolute top-0 left-0 p-2 hidden sm:block">
            <FastlyImage
              src={`/images/home/shine-star.png`}
              alt={'shine-star'}
              className="h-11 w-11 lg:h-12 lg:w-12 object-cover object-top"
            />
          </div>
          <div className="space-y-2">
            <p className="text-xl font-600 -mt-4 sm:mt-0">
              <Trans>
                Every student at Codingal gets these amazing benefits!
              </Trans>
            </p>
            <ul className="pl-5 md:pl-8">
              {(features ?? COURSE_FEATURES).map((feature, index) => (
                <li key={index} className="text-lg font-400 mb-2 text-blue-200">
                  <span className="text-grey">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-2 text-center">
          <Trans>
            Start with a free trial lesson at your preferred date & time. No
            commitments, fees, or credit card required.
          </Trans>
        </p>
        <div className="flex justify-center mt-2 w-full">
          <BookFreeClassBtn
            query={urlQuery}
            size={ctaSize ?? (compact ? 'md' : 'xl')}
            variant="solid"
          />
        </div>
      </div>
    </div>
  )
}

export default React.memo(CodingalSubscription)
